import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Divider,
  LinearProgress,
  InputAdornment,
  Checkbox
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Schedule as ScheduleIcon,
  Error as ErrorIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Calculate as CalculateIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { GeneratedStory, StoryStatus, TShirtSize, JobStatus, EstimationConfig, EstimationMethodology } from '../types/estimation';
import MLRecommendationCard from '../components/Estimation/MLRecommendationCard';

const JobDetails: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const [job, setJob] = useState<any>(null);
  const [stories, setStories] = useState<GeneratedStory[]>([]);
  const [loading, setLoading] = useState(true);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedStory, setSelectedStory] = useState<GeneratedStory | null>(null);
  const [reviewStatus, setReviewStatus] = useState<StoryStatus>(StoryStatus.PENDING);
  const [rejectionReason, setRejectionReason] = useState('');
  const [modifications, setModifications] = useState('');
  const [editingStoryId, setEditingStoryId] = useState<string | null>(null);
  const [editingStory, setEditingStory] = useState<Partial<GeneratedStory>>({});
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newStory, setNewStory] = useState<Partial<GeneratedStory>>({
    title: '',
    description: '',
    acceptanceCriteria: '',
    estimatedSize: TShirtSize.M
  });
  const [reviewMode, setReviewMode] = useState(true);
  const [allStoriesReviewed, setAllStoriesReviewed] = useState(false);
  const [reviewedStories, setReviewedStories] = useState<Set<string>>(new Set());
  const [estimationConfigs, setEstimationConfigs] = useState<EstimationConfig[]>([]);
  const [selectedConfigId, setSelectedConfigId] = useState<string>('');
  const [showConfigSelector, setShowConfigSelector] = useState(false);
  const [templates, setTemplates] = useState<any[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [selectedStoryIds, setSelectedStoryIds] = useState<string[]>([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  useEffect(() => {
    if (jobId) {
      loadJobDetails();
      loadEstimationConfigs();
      loadTemplates();
    }
  }, [jobId]);

  const loadEstimationConfigs = async () => {
    try {
      const configs = await apiService.getEstimationConfigs();
      setEstimationConfigs(configs);
      // Default to first config if available
      if (configs.length > 0 && !selectedConfigId) {
        setSelectedConfigId(configs[0].id);
      }
    } catch (error) {
      console.error('Error loading estimation configs:', error);
    }
  };

  const loadTemplates = async () => {
    try {
      const templatesData = await apiService.getEstimationTemplates();
      setTemplates(templatesData.data || templatesData || []);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const loadJobDetails = async () => {
    try {
      setLoading(true);
      const data = await apiService.getStoryGenerationJob(jobId!);
      setJob(data.job);
      setStories(data.stories);
      setSelectedStoryIds([]);
      
      // Check if all stories are reviewed
      const pendingStories = stories.filter(story => story.status === StoryStatus.PENDING);
      setAllStoriesReviewed(pendingStories.length === 0);
      
      // Initialize reviewed stories set
      const reviewed = new Set<string>();
      stories.forEach(story => {
        if (story.status !== StoryStatus.PENDING) {
          reviewed.add(story.id);
        }
      });
      setReviewedStories(reviewed);
    } catch (error) {
      console.error('Error loading job details:', error);
    } finally {
      setLoading(false);
    }
  };

  const allStoriesSelected = stories.length > 0 && selectedStoryIds.length === stories.length;
  const someStoriesSelected = selectedStoryIds.length > 0 && !allStoriesSelected;
  const isStorySelected = (storyId: string) => selectedStoryIds.includes(storyId);

  const toggleStorySelection = (storyId: string) => {
    setSelectedStoryIds(prev =>
      prev.includes(storyId) ? prev.filter(id => id !== storyId) : [...prev, storyId]
    );
  };

  const toggleSelectAllStories = () => {
    if (allStoriesSelected) {
      setSelectedStoryIds([]);
    } else {
      setSelectedStoryIds(stories.map(story => story.id));
    }
  };

  const clearStorySelection = () => {
    setSelectedStoryIds([]);
  };

  const handleBulkReview = async (status: StoryStatus) => {
    if (!jobId) return;
    if (selectedStoryIds.length === 0) {
      alert('Please select at least one story.');
      return;
    }

    let rejectionReason: string | undefined;
    let modifications: string | undefined;

    if (status === StoryStatus.REJECTED) {
      rejectionReason = window.prompt('Provide a reason for rejecting the selected stories:') || undefined;
      if (!rejectionReason) {
        alert('Rejection reason is required.');
        return;
      }
    } else if (status === StoryStatus.MODIFIED) {
      modifications = window.prompt('Provide modification notes for the selected stories:') || undefined;
    }

    try {
      setBulkActionLoading(true);
      const response = await apiService.bulkReviewStories(jobId, selectedStoryIds, status, {
        rejectionReason,
        modifications
      });
      if (response?.success) {
        await loadJobDetails();
        clearStorySelection();
        const updatedCount = response.data?.updated || selectedStoryIds.length;
        const actionLabel =
          status === StoryStatus.ACCEPTED
            ? 'approved'
            : status === StoryStatus.REJECTED
              ? 'rejected'
              : 'updated';
        alert(`Successfully ${actionLabel} ${updatedCount} stories.`);
      } else {
        alert(response?.message || 'Failed to update stories.');
      }
    } catch (error) {
      console.error('Bulk review failed:', error);
      alert('Failed to update selected stories.');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleReviewStory = (story: GeneratedStory) => {
    setSelectedStory(story);
    setReviewStatus(StoryStatus.PENDING);
    setRejectionReason('');
    setModifications('');
    setReviewDialogOpen(true);
  };

  const handleSubmitReview = async () => {
    if (!selectedStory) return;

    try {
      const reviewData: any = { status: reviewStatus };
      
      if (reviewStatus === StoryStatus.REJECTED && rejectionReason) {
        reviewData.rejectionReason = rejectionReason;
      } else if (reviewStatus === StoryStatus.MODIFIED && modifications) {
        reviewData.modifications = modifications;
      }

      await apiService.reviewStory(selectedStory.id, reviewData);
      
      // Add to reviewed stories set
      setReviewedStories(prev => new Set(Array.from(prev).concat(selectedStory.id)));
      
      // Reset form and close dialog
      setReviewDialogOpen(false);
      setSelectedStory(null);
      setReviewStatus(StoryStatus.PENDING);
      setRejectionReason('');
      setModifications('');
      
      // Reload data
      await loadJobDetails();
    } catch (error) {
      console.error('Error reviewing story:', error);
    }
  };

  const handleStartEditing = (story: GeneratedStory) => {
    setEditingStoryId(story.id);
    const selectedConfig = estimationConfigs.find(c => c.id === selectedConfigId);
    const methodology = selectedConfig?.methodology;

    setEditingStory({
      title: story.title,
      description: story.description,
      acceptanceCriteria: story.acceptanceCriteria,
      estimatedSize: story.estimatedSize,
      complexityFactors: story.complexityFactors ? { ...story.complexityFactors } : undefined,
      // Include methodology-specific data
      functionPointData: story.functionPointData || undefined,
      estimatedLinesOfCode: story.estimatedLinesOfCode || undefined
    });
  };

  const handleCancelEditing = () => {
    setEditingStoryId(null);
    setEditingStory({});
  };

  const handleSaveEditing = async () => {
    if (!editingStoryId || !editingStory) return;

    try {
      // Update the story with edited data
      const updatedStory = await apiService.updateGeneratedStory(editingStoryId, editingStory);
      
      // Update local state
      setStories(prev => prev.map(story => 
        story.id === editingStoryId ? { ...story, ...updatedStory } : story
      ));
      
      // Exit edit mode
      setEditingStoryId(null);
      setEditingStory({});
      
      // Reload job statistics
      await loadJobDetails();
    } catch (error) {
      console.error('Error updating story:', error);
    }
  };

  const handleEditField = (field: keyof GeneratedStory, value: any) => {
    setEditingStory(prev => ({ ...prev, [field]: value }));
  };

  const handleEditFunctionPointData = (type: string, complexity: string, value: number) => {
    setEditingStory(prev => {
      if (!prev) return prev;
      
      const currentFPData = prev.functionPointData || {
        externalInputs: { simple: 0, average: 0, complex: 0 },
        externalOutputs: { simple: 0, average: 0, complex: 0 },
        externalInquiries: { simple: 0, average: 0, complex: 0 },
        internalLogicalFiles: { simple: 0, average: 0, complex: 0 },
        externalInterfaceFiles: { simple: 0, average: 0, complex: 0 }
      };
      
      return {
        ...prev,
        functionPointData: {
          ...currentFPData,
          [type]: {
            ...currentFPData[type as keyof typeof currentFPData],
            [complexity]: Math.max(0, value)
          }
        }
      };
    });
  };

  const handleEditLinesOfCode = (value: number) => {
    setEditingStory(prev => ({
      ...prev,
      estimatedLinesOfCode: Math.max(0, value)
    }));
  };

  const handleEditComplexityFactor = (factor: string, value: number) => {
    setEditingStory(prev => {
      if (!prev) return prev;
      
      const currentFactors = prev.complexityFactors || {
        frontend: 3,
        backend: 3,
        middleware: 2,
        database: 2
      };
      
      return {
        ...prev,
        complexityFactors: {
          ...currentFactors,
          [factor]: Math.max(1, Math.min(5, value)) // Ensure value is between 1-5
        }
      };
    });
  };

  const handleApplyMLRecommendation = (recommendation: any) => {
    if (!editingStoryId) return;
    
    setEditingStory(prev => ({
      ...prev,
      estimatedSize: recommendation.recommendedTShirtSize || prev.estimatedSize,
      // You could also store recommendedStoryPoints if needed
    }));
  };

  const handleApplyMLRecommendationNew = (recommendation: any) => {
    setNewStory(prev => ({
      ...prev,
      estimatedSize: recommendation.recommendedTShirtSize || prev.estimatedSize,
    }));
  };

  const handleAddNewStory = () => {
    setIsAddingNew(true);
    setNewStory({
      title: '',
      description: '',
      acceptanceCriteria: '',
      estimatedSize: TShirtSize.M
    });
  };

  const handleCancelAddNew = () => {
    setIsAddingNew(false);
    setNewStory({
      title: '',
      description: '',
      acceptanceCriteria: '',
      estimatedSize: TShirtSize.M
    });
  };

  const handleSaveNewStory = async () => {
    if (!newStory.title?.trim()) {
      alert('Story title is required');
      return;
    }

    try {
      const createdStory = await apiService.createGeneratedStory(jobId!, newStory);
      
      // Add the new story to the list
      setStories(prev => [...prev, createdStory]);
      
      // Reset form and exit add mode
      setIsAddingNew(false);
      setNewStory({
        title: '',
        description: '',
        acceptanceCriteria: '',
        estimatedSize: TShirtSize.M
      });
      
      // Reload job statistics
      await loadJobDetails();
    } catch (error) {
      console.error('Error creating new story:', error);
    }
  };

  const handleNewStoryFieldChange = (field: keyof GeneratedStory, value: any) => {
    setNewStory(prev => ({ ...prev, [field]: value }));
  };

  // Review submission functions
  const handleSubmitAllReviews = async () => {
    const pendingStories = stories.filter(story => story.status === StoryStatus.PENDING);
    if (pendingStories.length > 0) {
      alert(`Please review all ${pendingStories.length} pending stories before submitting.`);
      return;
    }

    try {
      // Navigate to estimation page - user will select methodology there
      navigate(`/estimation/job/${jobId}/estimation`);
    } catch (error) {
      console.error('Error submitting reviews:', error);
    }
  };

  const getPendingStoriesCount = () => {
    return stories.filter(story => story.status === StoryStatus.PENDING).length;
  };

  const getReviewedStoriesCount = () => {
    return reviewedStories.size;
  };

  const approvedStories = stories.filter(story => story.status === StoryStatus.ACCEPTED);

  const getStatusColor = (status: StoryStatus) => {
    switch (status) {
      case StoryStatus.ACCEPTED:
        return 'success';
      case StoryStatus.REJECTED:
        return 'error';
      case StoryStatus.MODIFIED:
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: StoryStatus) => {
    switch (status) {
      case StoryStatus.ACCEPTED:
        return <CheckCircleIcon />;
      case StoryStatus.REJECTED:
        return <CancelIcon />;
      case StoryStatus.MODIFIED:
        return <EditIcon />;
      default:
        return <ScheduleIcon />;
    }
  };

  const getTShirtSizeColor = (size: TShirtSize) => {
    switch (size) {
      case TShirtSize.XS:
        return 'default';
      case TShirtSize.S:
        return 'primary';
      case TShirtSize.M:
        return 'secondary';
      case TShirtSize.L:
        return 'warning';
      case TShirtSize.XL:
        return 'error';
      case TShirtSize.XXL:
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateText = (text: string, maxLength: number = 100) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!job) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Job not found</Alert>
      </Box>
    );
  }

  // Compute selected methodology to ensure reactivity
  const selectedConfig = estimationConfigs.find(c => c.id === selectedConfigId);
  const selectedMethodology = selectedConfig?.methodology;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate('/estimation')}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {job.name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              icon={job.status === JobStatus.PROCESSING ? <CircularProgress size={16} /> : undefined}
              label={job.status}
              color={job.status === JobStatus.COMPLETED ? 'success' : job.status === JobStatus.FAILED ? 'error' : 'default'}
              size="small"
            />
            <Typography variant="body2" color="text.secondary">
              Created: {formatDate(job.createdAt)}
            </Typography>
            {job.project && (
              <Chip label={job.project.name} size="small" variant="outlined" />
            )}
          </Box>
        </Box>
        <IconButton onClick={loadJobDetails} title="Refresh">
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Job Information Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="text.secondary">
                File Name
              </Typography>
              <Typography variant="body1">
                {job.fileName}
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="text.secondary">
                Total Stories
              </Typography>
              <Typography variant="body1">
                {job.totalStories}
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="text.secondary">
                Accepted
              </Typography>
              <Typography variant="body1" color="success.main">
                {job.acceptedStories}
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="text.secondary">
                Rejected
              </Typography>
              <Typography variant="body1" color="error.main">
                {job.rejectedStories}
              </Typography>
            </Grid>
          </Grid>

          {job.status === JobStatus.PROCESSING && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Processing...
              </Typography>
              <LinearProgress />
            </Box>
          )}

          {job.errorMessage && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {job.errorMessage}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Step 1: Select Estimation Methodology & Template - FIRST, before review */}
      {stories.length > 0 && (
        <Card sx={{ mb: 3, border: 2, borderColor: 'primary.main', bgcolor: 'primary.50' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
              <AssessmentIcon color="primary" />
              Step 1: Select Estimation Methodology & Template
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              <strong>Choose your estimation technique and template FIRST.</strong> This will determine what data you need to collect for each story in the next step.
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Estimation Configuration *</InputLabel>
                  <Select
                    value={selectedConfigId}
                    onChange={(e) => setSelectedConfigId(e.target.value)}
                    label="Estimation Configuration *"
                    required
                  >
                    {estimationConfigs.map((config) => (
                      <MenuItem key={config.id} value={config.id}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {config.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {config.methodology.replace('_', ' ')} • {config.teamVelocity} velocity
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {selectedConfigId && (
                  <Box sx={{ mt: 1 }}>
                    <Chip 
                      label={estimationConfigs.find(c => c.id === selectedConfigId)?.methodology?.replace('_', ' ') || ''}
                      size="small"
                      color="primary"
                      sx={{ fontWeight: 'bold' }}
                    />
                  </Box>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <Button
                  variant="outlined"
                  onClick={() => setShowConfigSelector(true)}
                  fullWidth
                  startIcon={<AssessmentIcon />}
                  sx={{ height: 56 }}
                  color="primary"
                >
                  Apply Template (Optional)
                </Button>
              </Grid>
            </Grid>

            {selectedConfigId && (
              <Alert severity="success" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  ✅ <strong>Estimation Methodology Selected:</strong> {estimationConfigs.find(c => c.id === selectedConfigId)?.methodology?.replace('_', ' ')}
                  <br />
                  📋 <strong>Configuration:</strong> {estimationConfigs.find(c => c.id === selectedConfigId)?.name}
                  <br />
                  <small>Now proceed to Step 2 below to review stories, then Step 3 to collect methodology-specific data.</small>
                </Typography>
              </Alert>
            )}
            
            {!selectedConfigId && estimationConfigs.length === 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  No estimation configurations available. Please create one first from the Estimation Config page.
                </Typography>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Template Selection Dialog */}
      <Dialog open={showConfigSelector} onClose={() => setShowConfigSelector(false)} maxWidth="md" fullWidth>
        <DialogTitle>Apply Estimation Template</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select a template to apply predefined estimation settings. This will update your selected configuration.
            </Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select Template</InputLabel>
              <Select
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                label="Select Template"
              >
                <MenuItem value="">None</MenuItem>
                {templates.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    <Box>
                      <Typography variant="body2">{template.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {template.description || template.templateType}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedTemplateId && (
              <Alert severity="info">
                <Typography variant="body2">
                  This template will be applied to your current configuration. You can customize it after applying.
                </Typography>
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfigSelector(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (selectedTemplateId && selectedConfigId) {
                try {
                  const config = estimationConfigs.find(c => c.id === selectedConfigId);
                  if (config) {
                    const response = await apiService.applyTemplate(
                      selectedTemplateId,
                      config,
                      jobId
                    );
                    if (response?.success) {
                      await Promise.all([loadEstimationConfigs(), loadJobDetails()]);
                      setShowConfigSelector(false);
                      const storiesAdded = response.data?.storiesAdded || 0;
                      if (storiesAdded > 0) {
                        alert(`Template applied successfully! ${storiesAdded} sample stories were added for review.`);
                      } else {
                        alert('Template applied successfully!');
                      }
                    } else {
                      alert(response?.message || 'Failed to apply template');
                    }
                  }
                } catch (error) {
                  console.error('Error applying template:', error);
                  alert('Failed to apply template');
                }
              } else {
                alert('Please select a template and ensure a configuration is selected');
              }
            }}
            disabled={!selectedTemplateId || !selectedConfigId}
          >
            Apply Template
          </Button>
        </DialogActions>
      </Dialog>

      {/* Review Progress Section */}
      {stories.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Typography variant="h6">Step 2: Review Stories</Typography>
              <Chip 
                label={`${getReviewedStoriesCount()}/${stories.length} reviewed`} 
                color={getReviewedStoriesCount() === stories.length ? "success" : "warning"}
                size="small" 
              />
            </Box>
            
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Pending Reviews:
                  </Typography>
                  <Chip 
                    label={getPendingStoriesCount()}
                    color={getPendingStoriesCount() === 0 ? "success" : "warning"}
                    variant="outlined"
                  />
                  <Typography variant="body2" color="text.secondary">
                    Approved:
                  </Typography>
                  <Chip 
                    label={stories.filter(s => s.status === StoryStatus.ACCEPTED).length}
                    color="success"
                    variant="outlined"
                  />
                  <Typography variant="body2" color="text.secondary">
                    Rejected:
                  </Typography>
                  <Chip 
                    label={stories.filter(s => s.status === StoryStatus.REJECTED).length}
                    color="error"
                    variant="outlined"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                  <Button
                    variant="contained"
                    onClick={() => navigate(`/estimation/job/${jobId}/estimation?configId=${selectedConfigId}&autoCalculate=true`)}
                    disabled={!selectedConfigId}
                    startIcon={<AssessmentIcon />}
                    fullWidth
                    color="primary"
                  >
                    {selectedConfigId 
                      ? `Calculate Estimation (${approvedStories.length} Approved Stories)`
                      : 'Please Select Estimation Methodology First'
                    }
                  </Button>
                  
                  {getPendingStoriesCount() > 0 && (
                    <Button
                      variant="outlined"
                      onClick={handleSubmitAllReviews}
                      fullWidth
                      color="success"
                      startIcon={<CheckCircleIcon />}
                    >
                      Review {getPendingStoriesCount()} More Stories
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
            
            {/* Info Alert about Workflow */}
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2" component="div">
                <strong>Workflow:</strong>
                <br />1. Select estimation methodology above ⬆️
                <br />2. Review and approve stories below ⬇️
                <br />3. Click "Proceed to Data Collection" to collect methodology-specific data
                <br />4. Calculate estimates
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Stories Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Typography variant="h6">
            Generated Stories ({stories.length})
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
            {selectedStoryIds.length > 0 && (
              <>
                <Chip label={`${selectedStoryIds.length} selected`} color="info" variant="outlined" />
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  onClick={() => handleBulkReview(StoryStatus.ACCEPTED)}
                  disabled={bulkActionLoading}
                >
                  Approve Selected
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => handleBulkReview(StoryStatus.REJECTED)}
                  disabled={bulkActionLoading}
                >
                  Reject Selected
                </Button>
                <Button
                  variant="text"
                  size="small"
                  onClick={clearStorySelection}
                  disabled={bulkActionLoading}
                >
                  Clear
                </Button>
              </>
            )}
            <Tooltip title="Get AI recommendations for all pending stories">
              <Button
                variant="outlined"
                startIcon={<CalculateIcon />}
                onClick={() => {
                  // Batch AI suggestions - could be implemented as a modal or auto-trigger
                  alert('Batch AI suggestions: This feature will analyze all pending stories and provide recommendations. Feature coming soon!');
                }}
                size="small"
                color="primary"
              >
                Get AI Suggestions
              </Button>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddNewStory}
              disabled={isAddingNew}
              size="small"
            >
              Add New Story
            </Button>
          </Box>
        </Box>

        {stories.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Alert severity="info">
              No stories generated yet. {job.status === JobStatus.PROCESSING ? 'Processing in progress...' : 'Check the job status.'}
            </Alert>
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      indeterminate={someStoriesSelected}
                      checked={allStoriesSelected && stories.length > 0}
                      onChange={toggleSelectAllStories}
                      disabled={bulkActionLoading || stories.length === 0}
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ width: 60 }}>#</TableCell>
                  <TableCell>Story Title</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Acceptance Criteria</TableCell>
                  <TableCell>Size</TableCell>
                  {selectedMethodology === EstimationMethodology.FUNCTION_POINTS && (
                    <TableCell>FP Data</TableCell>
                  )}
                  {selectedMethodology === EstimationMethodology.COCOMO && (
                    <TableCell>LOC Estimate</TableCell>
                  )}
                  {selectedMethodology === EstimationMethodology.T_SHIRT_SIZING && (
                    <TableCell>Complexity</TableCell>
                  )}
                  {!selectedMethodology && (
                    <TableCell>Methodology Data</TableCell>
                  )}
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                                 {stories.map((story, index) => {
                   const isEditing = editingStoryId === story.id;
                   const storyData = isEditing ? editingStory : story;
                   
                   return (
                    <React.Fragment key={story.id}>
                    <TableRow hover selected={isStorySelected(story.id)}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          size="small"
                          checked={isStorySelected(story.id)}
                          onChange={() => toggleStorySelection(story.id)}
                          disabled={bulkActionLoading}
                        />
                      </TableCell>
                      <TableCell align="center">
                         <Typography variant="body2" sx={{ fontWeight: 500 }}>
                           {index + 1}
                         </Typography>
                       </TableCell>
                       <TableCell>
                         {isEditing ? (
                           <TextField
                             size="small"
                             value={storyData.title || ''}
                             onChange={(e) => handleEditField('title', e.target.value)}
                             fullWidth
                             variant="outlined"
                           />
                         ) : (
                           <Typography variant="body2" sx={{ fontWeight: 500 }}>
                             {story.title}
                           </Typography>
                         )}
                       </TableCell>
                       <TableCell>
                         {isEditing ? (
                           <TextField
                             size="small"
                             value={storyData.description || ''}
                             onChange={(e) => handleEditField('description', e.target.value)}
                             fullWidth
                             multiline
                             rows={2}
                             variant="outlined"
                           />
                         ) : (
                           <Typography variant="body2" color="text.secondary">
                             {truncateText(story.description || '', 80)}
                           </Typography>
                         )}
                       </TableCell>
                       <TableCell>
                         {isEditing ? (
                           <TextField
                             size="small"
                             value={storyData.acceptanceCriteria || ''}
                             onChange={(e) => handleEditField('acceptanceCriteria', e.target.value)}
                             fullWidth
                             multiline
                             rows={3}
                             variant="outlined"
                           />
                         ) : (
                           <Typography variant="body2" color="text.secondary">
                             {truncateText(story.acceptanceCriteria || '', 80)}
                           </Typography>
                         )}
                       </TableCell>
                       <TableCell>
                         {isEditing ? (
                           <FormControl size="small" fullWidth>
                             <Select
                               value={storyData.estimatedSize || TShirtSize.M}
                               onChange={(e) => handleEditField('estimatedSize', e.target.value)}
                             >
                               <MenuItem value={TShirtSize.XS}>XS</MenuItem>
                               <MenuItem value={TShirtSize.S}>S</MenuItem>
                               <MenuItem value={TShirtSize.M}>M</MenuItem>
                               <MenuItem value={TShirtSize.L}>L</MenuItem>
                               <MenuItem value={TShirtSize.XL}>XL</MenuItem>
                               <MenuItem value={TShirtSize.XXL}>XXL</MenuItem>
                             </Select>
                           </FormControl>
                         ) : (
                           story.estimatedSize && (
                             <Chip
                               label={story.estimatedSize.toUpperCase()}
                               color={getTShirtSizeColor(story.estimatedSize) as any}
                               size="small"
                             />
                           )
                         )}
                       </TableCell>
                       {/* Methodology-Specific Column */}
                       {selectedMethodology === EstimationMethodology.FUNCTION_POINTS && (() => {
                         const fpData = story.functionPointData;
                         const hasData = fpData && (
                           fpData.externalInputs.simple + fpData.externalInputs.average + fpData.externalInputs.complex +
                           fpData.externalOutputs.simple + fpData.externalOutputs.average + fpData.externalOutputs.complex +
                           fpData.externalInquiries.simple + fpData.externalInquiries.average + fpData.externalInquiries.complex +
                           fpData.internalLogicalFiles.simple + fpData.internalLogicalFiles.average + fpData.internalLogicalFiles.complex +
                           fpData.externalInterfaceFiles.simple + fpData.externalInterfaceFiles.average + fpData.externalInterfaceFiles.complex
                         ) > 0;
                         
                         return (
                           <TableCell>
                             {hasData ? (
                               <Chip label="FP Data ✓" color="success" size="small" />
                             ) : (
                               <Chip label="No FP Data" color="default" size="small" variant="outlined" />
                             )}
                           </TableCell>
                         );
                       })()}
                       {selectedMethodology === EstimationMethodology.COCOMO && (() => {
                         const loc = story.estimatedLinesOfCode;
                         return (
                           <TableCell>
                             {loc && loc > 0 ? (
                               <Typography variant="body2">
                                 {loc.toLocaleString()} LOC
                                 <br />
                                 <small>({(loc / 1000).toFixed(2)} KLOC)</small>
                               </Typography>
                             ) : (
                               <Chip label="No LOC" color="default" size="small" variant="outlined" />
                             )}
                           </TableCell>
                         );
                       })()}
                       {selectedMethodology === EstimationMethodology.T_SHIRT_SIZING && (() => {
                         const factors = story.complexityFactors;
                         if (factors) {
                           const avgComplexity = (factors.frontend + factors.backend + factors.middleware + factors.database) / 4;
                           return (
                             <TableCell>
                               <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                 F: {factors.frontend} | B: {factors.backend}
                                 <br />
                                 M: {factors.middleware} | D: {factors.database}
                                 <br />
                                 <strong>Avg: {avgComplexity.toFixed(1)}</strong>
                               </Typography>
                             </TableCell>
                           );
                         }
                         return (
                           <TableCell>
                             <Chip label="No Data" color="default" size="small" variant="outlined" />
                           </TableCell>
                         );
                       })()}
                       {!selectedMethodology && (
                         <TableCell>
                           <Chip label="Select Config" color="warning" size="small" variant="outlined" />
                         </TableCell>
                       )}
                       <TableCell>
                         <Chip
                           icon={getStatusIcon(story.status)}
                           label={story.status}
                           color={getStatusColor(story.status) as any}
                           size="small"
                         />
                       </TableCell>
                       <TableCell align="center">
                         <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                           {isEditing ? (
                             <>
                               <Tooltip title="Save Changes">
                                 <IconButton
                                   size="small"
                                   color="success"
                                   onClick={handleSaveEditing}
                                 >
                                   <SaveIcon />
                                 </IconButton>
                               </Tooltip>
                               <Tooltip title="Cancel Editing">
                                 <IconButton
                                   size="small"
                                   color="error"
                                   onClick={handleCancelEditing}
                                 >
                                   <CloseIcon />
                                 </IconButton>
                               </Tooltip>
                               {(story.status === StoryStatus.PENDING || story.status === StoryStatus.MODIFIED) && (
                                 <Tooltip title="Review Story">
                                   <IconButton
                                     size="small"
                                     color="secondary"
                                     onClick={() => handleReviewStory(story)}
                                   >
                                     <CheckCircleIcon />
                                   </IconButton>
                                 </Tooltip>
                               )}
                             </>
                           ) : (
                             <>
                               <Tooltip title="Edit Story">
                                 <IconButton
                                   size="small"
                                   color="primary"
                                   onClick={() => handleStartEditing(story)}
                                 >
                                   <EditIcon />
                                 </IconButton>
                               </Tooltip>
                               {(story.status === StoryStatus.PENDING || story.status === StoryStatus.MODIFIED) && (
                                 <Tooltip title="Review Story">
                                   <IconButton
                                     size="small"
                                     color="secondary"
                                     onClick={() => handleReviewStory(story)}
                                   >
                                     <CheckCircleIcon />
                                   </IconButton>
                                 </Tooltip>
                              )}
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                    {/* Methodology-Specific Fields & ML Recommendation - shown when editing */}
                    {isEditing && (
                      <TableRow>
                        <TableCell colSpan={selectedMethodology ? 8 : 7} sx={{ pt: 0, pb: 2 }}>
                          <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                            <CardContent>
                              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                Methodology-Specific Data: {selectedMethodology?.replace('_', ' ').toUpperCase() || 'NOT SELECTED'}
                              </Typography>
                              
                              {selectedMethodology === EstimationMethodology.FUNCTION_POINTS && (
                                  <Box sx={{ mt: 2 }}>
                                    <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
                                      Function Point Analysis Data
                                    </Typography>
                                    <Table size="small" sx={{ mt: 1 }}>
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Type</TableCell>
                                          <TableCell align="center">Simple</TableCell>
                                          <TableCell align="center">Average</TableCell>
                                          <TableCell align="center">Complex</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {['externalInputs', 'externalOutputs', 'externalInquiries', 'internalLogicalFiles', 'externalInterfaceFiles'].map((type) => {
                                          const fpData = editingStory.functionPointData || story.functionPointData || {
                                            externalInputs: { simple: 0, average: 0, complex: 0 },
                                            externalOutputs: { simple: 0, average: 0, complex: 0 },
                                            externalInquiries: { simple: 0, average: 0, complex: 0 },
                                            internalLogicalFiles: { simple: 0, average: 0, complex: 0 },
                                            externalInterfaceFiles: { simple: 0, average: 0, complex: 0 }
                                          };
                                          const typeData = fpData[type as keyof typeof fpData] || { simple: 0, average: 0, complex: 0 };
                                          
                                          return (
                                            <TableRow key={type}>
                                              <TableCell>{type.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</TableCell>
                                              {['simple', 'average', 'complex'].map((complexity) => (
                                                <TableCell align="center" key={complexity}>
                                                  <TextField
                                                    type="number"
                                                    size="small"
                                                    value={typeData[complexity as keyof typeof typeData] || 0}
                                                    onChange={(e) => handleEditFunctionPointData(type, complexity, parseInt(e.target.value) || 0)}
                                                    inputProps={{ min: 0 }}
                                                    sx={{ width: 80 }}
                                                  />
                                                </TableCell>
                                              ))}
                                            </TableRow>
                                          );
                                        })}
                                      </TableBody>
                                    </Table>
                                  </Box>
                                )}
                                
                              {selectedMethodology === EstimationMethodology.COCOMO && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    Lines of Code Estimate
                                  </Typography>
                                  <TextField
                                    label="Estimated Lines of Code (LOC)"
                                    type="number"
                                    fullWidth
                                    size="small"
                                    value={editingStory.estimatedLinesOfCode || story.estimatedLinesOfCode || 0}
                                    onChange={(e) => handleEditLinesOfCode(parseFloat(e.target.value) || 0)}
                                    inputProps={{ min: 0, step: 100 }}
                                    helperText={`${(((editingStory.estimatedLinesOfCode || story.estimatedLinesOfCode || 0) / 1000).toFixed(2))} KLOC`}
                                    sx={{ mt: 1 }}
                                  />
                                </Box>
                              )}
                              
                              {selectedMethodology === EstimationMethodology.T_SHIRT_SIZING && (
                                  <Box sx={{ mt: 2 }}>
                                    <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
                                      Complexity Factors (1-5 scale)
                                    </Typography>
                                    <Grid container spacing={2} sx={{ mt: 1 }}>
                                      {['frontend', 'backend', 'middleware', 'database'].map((factor) => {
                                        const factors = editingStory.complexityFactors || story.complexityFactors || {
                                          frontend: 3,
                                          backend: 3,
                                          middleware: 2,
                                          database: 2
                                        };
                                        
                                        return (
                                          <Grid item xs={6} md={3} key={factor}>
                                            <TextField
                                              label={factor.charAt(0).toUpperCase() + factor.slice(1)}
                                              type="number"
                                              size="small"
                                              fullWidth
                                              value={factors[factor as keyof typeof factors] || 3}
                                              onChange={(e) => handleEditComplexityFactor(factor, parseInt(e.target.value) || 3)}
                                              inputProps={{ min: 1, max: 5 }}
                                            />
                                          </Grid>
                                        );
                                      })}
                                    </Grid>
                                  </Box>
                                )}
                                
                              {/* ML Recommendation Card */}
                              {selectedMethodology === EstimationMethodology.T_SHIRT_SIZING && (
                                  <Box sx={{ mt: 2 }}>
                                    <MLRecommendationCard
                                      story={{
                                        title: editingStory.title || story.title,
                                        description: editingStory.description || story.description,
                                        estimatedSize: editingStory.estimatedSize || story.estimatedSize,
                                        complexityFactors: editingStory.complexityFactors || story.complexityFactors || {
                                          frontend: 3,
                                          backend: 3,
                                          middleware: 2,
                                          database: 2
                                        }
                                      }}
                                      teamId={undefined}
                                      projectId={job?.project?.id}
                                      onApply={handleApplyMLRecommendation}
                                    />
                                  </Box>
                                )}
                            </CardContent>
                          </Card>
                        </TableCell>
                      </TableRow>
                    )}
                    </React.Fragment>
                  );
                })}
                 
                 {/* Add New Story Row */}
                {isAddingNew && (
                   <TableRow sx={{ backgroundColor: 'action.hover' }}>
                    <TableCell padding="checkbox" />
                     <TableCell align="center">
                       <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                         New
                       </Typography>
                     </TableCell>
                     <TableCell>
                       <TextField
                         size="small"
                         value={newStory.title || ''}
                         onChange={(e) => handleNewStoryFieldChange('title', e.target.value)}
                         fullWidth
                         variant="outlined"
                         placeholder="Enter story title"
                         required
                       />
                     </TableCell>
                     <TableCell>
                       <TextField
                         size="small"
                         value={newStory.description || ''}
                         onChange={(e) => handleNewStoryFieldChange('description', e.target.value)}
                         fullWidth
                         multiline
                         rows={2}
                         variant="outlined"
                         placeholder="Enter story description"
                       />
                     </TableCell>
                     <TableCell>
                       <TextField
                         size="small"
                         value={newStory.acceptanceCriteria || ''}
                         onChange={(e) => handleNewStoryFieldChange('acceptanceCriteria', e.target.value)}
                         fullWidth
                         multiline
                         rows={3}
                         variant="outlined"
                         placeholder="Enter acceptance criteria"
                       />
                     </TableCell>
                     <TableCell>
                       <FormControl size="small" fullWidth>
                         <Select
                           value={newStory.estimatedSize || TShirtSize.M}
                           onChange={(e) => handleNewStoryFieldChange('estimatedSize', e.target.value)}
                         >
                           <MenuItem value={TShirtSize.XS}>XS</MenuItem>
                           <MenuItem value={TShirtSize.S}>S</MenuItem>
                           <MenuItem value={TShirtSize.M}>M</MenuItem>
                           <MenuItem value={TShirtSize.L}>L</MenuItem>
                           <MenuItem value={TShirtSize.XL}>XL</MenuItem>
                           <MenuItem value={TShirtSize.XXL}>XXL</MenuItem>
                         </Select>
                       </FormControl>
                     </TableCell>
                     <TableCell>
                       <Chip
                         label="PENDING"
                         color="default"
                         size="small"
                       />
                     </TableCell>
                     <TableCell align="center">
                       <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                         <Tooltip title="Save New Story">
                           <IconButton
                             size="small"
                             color="success"
                             onClick={handleSaveNewStory}
                             disabled={!newStory.title?.trim()}
                           >
                             <SaveIcon />
                           </IconButton>
                         </Tooltip>
                         <Tooltip title="Cancel">
                           <IconButton
                             size="small"
                             color="error"
                             onClick={handleCancelAddNew}
                           >
                             <CloseIcon />
                           </IconButton>
                         </Tooltip>
                       </Box>
                     </TableCell>
                   </TableRow>
                 )}
                 {/* ML Recommendation Card for New Story */}
                 {isAddingNew && (
                   <TableRow>
                    <TableCell colSpan={8} sx={{ pt: 0, pb: 2 }}>
                       <MLRecommendationCard
                         story={{
                           title: newStory.title || '',
                           description: newStory.description || '',
                           estimatedSize: newStory.estimatedSize || TShirtSize.M,
                           complexityFactors: newStory.complexityFactors || {
                             frontend: 3,
                             backend: 3,
                             middleware: 2,
                             database: 2
                           }
                         }}
                         teamId={undefined}
                         projectId={job?.project?.id}
                         onApply={handleApplyMLRecommendationNew}
                       />
                     </TableCell>
                   </TableRow>
                 )}
               </TableBody>
             </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onClose={() => setReviewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Review Story: {selectedStory?.title}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Story Description:
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {selectedStory?.description}
            </Typography>

            {selectedStory?.acceptanceCriteria && (
              <>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Acceptance Criteria:
                </Typography>
                <Typography variant="body1" component="pre" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
                  {selectedStory.acceptanceCriteria}
                </Typography>
              </>
            )}
          </Box>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Review Action</InputLabel>
            <Select
              value={reviewStatus}
              onChange={(e) => setReviewStatus(e.target.value as StoryStatus)}
              label="Review Action"
            >
              <MenuItem value={StoryStatus.ACCEPTED}>Accept</MenuItem>
              <MenuItem value={StoryStatus.REJECTED}>Reject</MenuItem>
              <MenuItem value={StoryStatus.MODIFIED}>Modify</MenuItem>
            </Select>
          </FormControl>

          {reviewStatus === StoryStatus.REJECTED && (
            <TextField
              fullWidth
              label="Rejection Reason"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              multiline
              rows={3}
              sx={{ mb: 2 }}
            />
          )}

          {reviewStatus === StoryStatus.MODIFIED && (
            <TextField
              fullWidth
              label="Modifications"
              value={modifications}
              onChange={(e) => setModifications(e.target.value)}
              multiline
              rows={3}
              sx={{ mb: 2 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitReview}
            variant="contained"
            disabled={
              (reviewStatus === StoryStatus.REJECTED && !rejectionReason.trim()) ||
              (reviewStatus === StoryStatus.MODIFIED && !modifications.trim())
            }
          >
            Submit Review
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default JobDetails;
